/* eslint-disable react-hooks/exhaustive-deps */
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { Accordion, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { RegimeTypes } from "../../../config/filingConstants";
import { EToastvariant, IActiveTabs, IFormITR1 } from "../../../config/interfaces";
import { useAppDispatch, useAppSelector } from "../../../redux/Store";
import { ITRFilingSelector, setCheckOutPlan } from "../../../redux/slice/ITRFilingSlice";
import { showToastMsg } from "../../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../../services";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import {
  ReviewDeductions,
  ReviewIncomeDetails,
  ReviewPermanentDetails,
  ReviewSummaryCard,
  ReviewTaxDueOrRefundDetails,
  ReviewTaxPaidDetails,
  ReviewTaxPayableDetails
} from "../../itr-filing/review-file-details";
import { AlertModal, DisclaimerModal } from "../../modals";

type Props = {
  handleNaviagteTab: (tabName: IActiveTabs) => void;
  validationErrorMsg: () => void;
  activeTab?: IActiveTabs;
};

const ReviewAndFileTab: React.FC<Props> = (props: Props) => {
  const { handleNaviagteTab, validationErrorMsg, activeTab } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentFilingUUID, currentFilingPlan, selfPlansList } = useAppSelector(ITRFilingSelector);

  const { values, setValues, validateForm } = useFormikContext<IFormITR1>();
  const RegimeType = values.FilingReturnInfo.RegimeType;

  const [showAlertModal, setShowAlertModal] = useState<boolean>(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState<boolean>(false);

  useEffect(() => {
    if (activeTab !== "review-file") {
      return;
    }

    const _GrossTotalIncome =
      (values?.IncomeFromSal || 0) +
      (values?.TotalHousePropertyIncome || 0) +
      (values?.TotalBusOrProfIncome || 0) +
      (values?.TotalCapitalGains || 0) +
      (values?.IncomeOthSrc || 0) +
      (values?.TotalVirtualDigitalAsset || 0);

    const _SplRateIncome = values?.TotalCapitalGains || 0 - values.TotalNrlRateIncome;
    const _NormalIncome = _GrossTotalIncome - _SplRateIncome;
    const _TotalSplRateTax = values.TotalSplRateTax;

    const GTI = _NormalIncome - (values?.TotalChapVIADeductions || 0);
    const _TotalTaxableIncome = Math.round(GTI / 10) * 10; // Round Off to nearest 10

    let TaxDetails = {
      TotalTaxPayable: 0,
      Rebate87A: 0,
      TaxPayableOnRebate: 0,
      EducationCess: 0
    };

    if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
      TaxDetails = UtilService.calculateNewRegimeTax(_TotalTaxableIncome);
    } else {
      TaxDetails = UtilService.calculateOldRegimeTax(_TotalTaxableIncome, values.DOB);
    }

    const result = UtilService.getTaxAndTaxesPaidComputation(
      values,
      TaxDetails,
      RegimeType,
      _TotalTaxableIncome,
      _TotalSplRateTax
    );

    const DATA = {
      GrossTotIncome: Math.round(_GrossTotalIncome),
      TotalTaxableIncome: Math.round(_TotalTaxableIncome),
      ITR1_TaxComputation: { ...cloneDeep(result.TaxComputation) },
      TaxPaid: { ...cloneDeep(result.TaxPaidComputation) }
    };

    setValues({ ...cloneDeep(values), ...DATA });
  }, [activeTab, values.LastSavedTimeStamp]);

  const handleFileNowSubmit = async () => {
    try {
      const validationErrors = await validateForm();

      if (Object.keys(validationErrors).length === 0) {
        // When no plan exist, then navigate to purchase a plan
        if (!currentFilingPlan) {
          // show a disclaimer to purchase 499/- amount plan
          if (selfPlansList.length) {
            setShowDisclaimerModal(() => true);
          } else {
            dispatch(
              showToastMsg({
                isVisible: true,
                title: "Error",
                message: UtilService.getErrorMessage("No plan available to purchase."),
                variant: EToastvariant.Danger
              })
            );
          }
          return;
        }

        if (
          currentFilingPlan &&
          !currentFilingPlan?.inactive &&
          currentFilingPlan?.planData?.type === "self"
        ) {
          setShowAlertModal(() => true);
          return;
        }

        if (
          currentFilingPlan &&
          !currentFilingPlan?.inactive &&
          currentFilingPlan?.planData?.type === "assisted"
        ) {
          setShowAlertModal(() => true);
          return;
        }
      }
      validationErrorMsg();
      if (Object.keys(validationErrors).length > 0) {
        // Find the first element with an error
        const element = document.querySelector(`.info-missing`);
        if (!element) {
          return;
        }
        // Scroll to first known error into view
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    }
  };

  return (
    <>
      <ReviewSummaryCard handleFileNowSubmit={handleFileNowSubmit} />

      <h3 className="text-primary fw-bold mb-4">Tax Break-up</h3>
      <Accordion
        defaultActiveKey={[
          "review-file-0",
          "review-file-1",
          "review-file-2",
          "review-file-3",
          "review-file-4",
          "review-file-5"
        ]}
        alwaysOpen
      >
        <ReviewPermanentDetails handleOnPressEdit={(tabName) => handleNaviagteTab(tabName)} />

        <ReviewIncomeDetails handleOnPressEdit={(tabName) => handleNaviagteTab(tabName)} />

        <ReviewDeductions handleOnPressEdit={(tabName) => handleNaviagteTab(tabName)} />

        <ReviewTaxPayableDetails />

        <ReviewTaxPaidDetails handleOnPressEdit={(tabName) => handleNaviagteTab(tabName)} />

        <ReviewTaxDueOrRefundDetails />
      </Accordion>

      <PreventEditComponent>
        <div className="btns-wrap">
          <Button
            size="lg"
            type="button"
            variant="outline-greyDark"
            onClick={() => handleNaviagteTab("deductions")}
          >
            <FASvgIcon icon={faArrowLeft} /> Back
          </Button>
          <Button size="lg" type="button" variant="primary" onClick={() => handleFileNowSubmit()}>
            File Now <FASvgIcon icon={faArrowRight} />
          </Button>
        </div>
      </PreventEditComponent>

      <DisclaimerModal
        showModal={showDisclaimerModal}
        title="Disclaimer"
        message={
          <p>
            We are currently in the process of integrating with the Income Tax portal for DIY
            filing. In the meantime, you can fill in your details, and we will file your taxes for
            you. Please note, this service includes no advice or changes at&nbsp;
            <strong>
              <PriceDisplay price={499} />
            </strong>
          </p>
        }
        onYesBtnClick={() => {
          // Navigate to checkout with DIY (499/-) amount plan
          dispatch(setCheckOutPlan(selfPlansList[0]));
          navigate(`/itr-filings/${currentFilingUUID}/self/plan-checkout`);
        }}
        onYesBtnText="Proceed"
        onNoBtnClick={() => setShowDisclaimerModal(() => false)}
        onNoBtnText="Cancel"
      />

      <AlertModal
        showModal={showAlertModal}
        iconColorClass="text-success"
        title="Alert"
        message={<p>The file under process</p>}
        onYesBtnClick={() => setShowAlertModal(() => false)}
        onYesBtnText="Okay"
      />
    </>
  );
};

export default ReviewAndFileTab;
